<div class="appointment-builder-holder">
  <h2 mat-dialog-title>Create New Appointment</h2>
  
  <mat-dialog-content>
    <mat-stepper [linear]="false" #stepper orientation="horizontal">

      <!-- Step 1: Schedule -->
      <mat-step label="Schedule" [completed]="isScheduleStepComplete()">
        <div class="step-content">
          <p>Select the appointment type and schedule.</p>
          
          <form class="schedule-form">
            <!-- Appointment Type Selection (static enum options) -->
            <mat-form-field class="full-width" appearance="outline">
              <mat-label>Appointment Type</mat-label>
              <mat-select [(value)]="appointmentData.appointment_type" (selectionChange)="onAppointmentTypeChange($event)" required>
                @for (option of appointmentTypeOptions; track option.value) {
                  <mat-option [value]="option.value">{{ option.title }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Filtered Schedule Selection -->
            <mat-form-field class="full-width" appearance="outline">
              <mat-label>Schedule</mat-label>
              <mat-select [(value)]="appointmentData.scheduleId" [disabled]="!appointmentData.appointment_type" required>
                @if (filteredSchedules.length > 0) {
                  @for (schedule of filteredSchedules; track schedule.id) {
                    <mat-option [value]="schedule.id">{{ schedule.name }} - {{ schedule.user?.name }}</mat-option>
                  }
                } @else {
                  <mat-option disabled>
                    @if (appointmentData.appointment_type) {
                      No schedules available for {{ getAppointmentTypeDisplay(appointmentData.appointment_type) }}
                    } @else {
                      Please select appointment type first
                    }
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <div class="step-actions">
              <button mat-raised-button color="primary" matStepperNext [disabled]="!isScheduleStepComplete()">
                Next: Location
              </button>
            </div>
          </form>
        </div>
      </mat-step>

      <!-- Step 2: Location -->
      <mat-step label="Location" [completed]="isLocationStepComplete()">
        <div class="step-content">
          @if (appointmentData.appointment_type && appointmentData.scheduleId) {
            @if (isServiceAppointment()) {
              <!-- Service Appointment - ServiceAddress-first location step (Req 1.1/1.2) -->
              <p>Select or create the ServiceAddress for this service appointment.</p>

              <div class="step-actions">
                <button mat-raised-button type="button" (click)="openServiceAddressPopup()">
                  {{ selectedServiceAddress ? 'Change Service Address' : 'Select Service Address' }}
                </button>
              </div>

              @if (selectedServiceAddress) {
                <div class="location-preview">
                  <h4>Service Address:</h4>
                  <div class="address-display">
                    {{ formatAddressForDisplay(selectedServiceAddress.address) }}
                  </div>
                </div>

                @if (linkedSubsForServiceAddress.length > 1) {
                  <!-- Req 1.7 - more than one linked Sub, require an explicit choice -->
                  <mat-form-field class="full-width" appearance="outline">
                    <mat-label>Subscription</mat-label>
                    <mat-select [(value)]="appointmentData.subId" required>
                      @for (sub of linkedSubsForServiceAddress; track sub.id) {
                        <mat-option [value]="sub.id">{{ sub.name }}</mat-option>
                      }
                    </mat-select>
                    <mat-hint>This Service Address is linked to more than one subscription - choose which one this appointment is for.</mat-hint>
                  </mat-form-field>
                }
              }

            } @else {
              <!-- Sales Appointment - Select Lead (unchanged) -->
              <p>Select the lead for this sales appointment to determine the meeting location.</p>
              
              <mat-form-field class="full-width" appearance="outline">
                <mat-label>Lead</mat-label>
                <mat-select [(value)]="selectedLocationRecord" (selectionChange)="onLocationRecordChange($event)" required>
                  @if (leads.length > 0) {
                    @for (lead of leads; track lead.id) {
                      <mat-option [value]="lead">
                        {{ lead.name }} - {{ lead.email || lead.phone }}
                        @if (lead.zipCode) {
                          <small class="address-preview"> ({{ lead.zipCode }})</small>
                        }
                      </mat-option>
                    }
                  } @else {
                    <mat-option disabled>No leads found</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              @if (selectedLocationRecord) {
                <div class="location-preview">
                  <h4>Lead Information:</h4>
                  <div class="lead-display">
                    <p><strong>Name:</strong> {{ selectedLocationRecord.name }}</p>
                    @if (selectedLocationRecord.email) {
                      <p><strong>Email:</strong> {{ selectedLocationRecord.email }}</p>
                    }
                    @if (selectedLocationRecord.phone) {
                      <p><strong>Phone:</strong> {{ selectedLocationRecord.phone }}</p>
                    }
                    @if (selectedLocationRecord.zipCode) {
                      <p><strong>ZIP Code:</strong> {{ selectedLocationRecord.zipCode }}</p>
                    }
                  </div>
                </div>
              }

              <!-- ServiceAddress selection step (Req 1.2) - added alongside the
                   Lead step above; a Lead alone is not sufficient for SALES. -->
              <p>Select or create the ServiceAddress for this sales appointment.</p>

              <div class="step-actions">
                <button mat-raised-button type="button" (click)="openServiceAddressPopup()">
                  {{ selectedServiceAddress ? 'Change Service Address' : 'Select Service Address' }}
                </button>
              </div>

              @if (selectedServiceAddress) {
                <div class="location-preview">
                  <h4>Service Address:</h4>
                  <div class="address-display">
                    {{ formatAddressForDisplay(selectedServiceAddress.address) }}
                  </div>
                </div>
              }
            }
            
            <div class="step-actions">
              <button mat-button matStepperPrevious>Back</button>
              <button mat-raised-button color="primary" matStepperNext [disabled]="!isLocationStepComplete()">
                Next: Details
              </button>
            </div>
            
          } @else {
            <p class="warning-message">Please complete the schedule selection first.</p>
            <button mat-button matStepperPrevious>Back to Schedule</button>
          }
        </div>
      </mat-step>

      <!-- Step 3: Details -->
      <mat-step label="Details" [completed]="!!appointmentObj">
        <div class="step-content">
          <p>Enter the appointment details.</p>
          
          <form class="details-form" (ngSubmit)="onAppointmentFormSubmit()">
            <!-- Service Type (required) -->
            @if (serviceTypes.length > 0) {
              <mat-form-field class="full-width" appearance="outline">
                <mat-label>Service Type</mat-label>
                <mat-select [(value)]="appointmentData.serviceTypeId" (selectionChange)="onServiceTypeChange()" required>
                  @for (serviceType of serviceTypes; track serviceType.id) {
                    <mat-option [value]="serviceType.id">{{ serviceType.name }}</mat-option>
                  }
                </mat-select>
                <mat-hint>Determines the default appointment duration.</mat-hint>
              </mat-form-field>
            } @else {
              <p class="warning-message">
                No Service Types exist yet.
                <a routerLink="/dashboard/form/ServiceType/-1" target="_blank">Create one</a>
                first, then reopen this dialog.
              </p>
            }

            <!-- Services (multi-select) - SERVICE appointments only, scoped to the selected ServiceAddress -->
            @if (isServiceAppointment()) {
              @if (servicesForSelectedAddress.length > 0) {
                <mat-form-field class="full-width" appearance="outline">
                  <mat-label>Services</mat-label>
                  <mat-select [(value)]="appointmentData.serviceIds" (selectionChange)="onServicesChange()" multiple>
                    @for (service of servicesForSelectedAddress; track service.id) {
                      <mat-option [value]="service.id">{{ service.title }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              } @else {
                <p class="warning-message">
                  No Services are linked to this Service Address yet. Link a Service to it before creating this appointment.
                </p>
              }
            }

            <!-- Appointment Name -->
            <mat-form-field class="full-width" appearance="outline">
              <mat-label>Appointment Name</mat-label>
              <input matInput [(ngModel)]="appointmentData.name" name="name" required placeholder="Enter appointment name"
                     (ngModelChange)="onNameManuallyEdited()">
              <mat-hint>Auto-generated from the Property Owner, Services, Service Type, and month - edit freely if needed.</mat-hint>
            </mat-form-field>

            <!-- Start Date/Time -->
            <div class="datetime-row">
              <mat-form-field class="date-field" appearance="outline">
                <mat-label>Start Date</mat-label>
                <input matInput 
                       [matDatepicker]="startDatePicker"
                       [(ngModel)]="appointmentData.start_date" 
                       (ngModelChange)="onStartDateTimeChange()"
                       name="start_date" 
                       required>
                <mat-datepicker-toggle matIconSuffix [for]="startDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #startDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field class="time-field" appearance="outline">
                <mat-label>Start Time</mat-label>
                <input matInput 
                       [matTimepicker]="startTimePicker"
                       [matTimepickerMin]="cachedMinTime"
                       [matTimepickerMax]="cachedMaxTime"
                       [(ngModel)]="appointmentData.start_time" 
                       (ngModelChange)="onStartDateTimeChange()"
                       name="start_time" 
                       required>
                <mat-timepicker-toggle matIconSuffix [for]="startTimePicker"></mat-timepicker-toggle>
                <mat-timepicker #startTimePicker [interval]="getTimeStepMinutes()"></mat-timepicker>
                @if (timeRestrictions.minTime && timeRestrictions.maxTime) {
                  <mat-hint>Business hours: {{ timeRestrictions.minTime }} - {{ timeRestrictions.maxTime }}</mat-hint>
                }
              </mat-form-field>
            </div>

            <!-- End Date/Time -->
            <div class="datetime-row">
              <mat-form-field class="date-field" appearance="outline">
                <mat-label>End Date</mat-label>
                <input matInput 
                       [matDatepicker]="endDatePicker"
                       [(ngModel)]="appointmentData.end_date" 
                       name="end_date" 
                       required>
                <mat-datepicker-toggle matIconSuffix [for]="endDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #endDatePicker></mat-datepicker>
              </mat-form-field>

              <mat-form-field class="time-field" appearance="outline">
                <mat-label>End Time</mat-label>
                <input matInput 
                       [matTimepicker]="endTimePicker"
                       [matTimepickerMin]="cachedMinTime"
                       [matTimepickerMax]="cachedMaxTime"
                       [(ngModel)]="appointmentData.end_time" 
                       name="end_time" 
                       required>
                <mat-timepicker-toggle matIconSuffix [for]="endTimePicker"></mat-timepicker-toggle>
                <mat-timepicker #endTimePicker [interval]="getTimeStepMinutes()"></mat-timepicker>
                @if (timeRestrictions.minTime && timeRestrictions.maxTime) {
                  <mat-hint>Business hours: {{ timeRestrictions.minTime }} - {{ timeRestrictions.maxTime }}</mat-hint>
                }
              </mat-form-field>
            </div>

            <!-- Subscription Link -->
            @if (isServiceAppointment()) {
              <!-- Read-only for SERVICE appointments - the Sub reference is
                   already derived from the selected ServiceAddress in the
                   Location step (see deriveSubFromServiceAddress()), so it
                   must not be changeable here. -->
              <mat-form-field class="full-width" appearance="outline">
                <mat-label>Related Subscription</mat-label>
                <input matInput [value]="relatedSubscriptionDisplay" disabled>
              </mat-form-field>
            } @else if (selectedLocationRecord) {
              <!-- SALES appointment with a Lead selected - auto-populated, read-only -->
              <mat-form-field class="full-width" appearance="outline">
                <mat-label>Related Subscription</mat-label>
                <input matInput [value]="selectedLocationRecord.name + ' - ' + selectedLocationRecord.customer?.name" disabled>
              </mat-form-field>
            } @else {
              <!-- Manual subscription selection for CONSULTATION/SALES-without-Lead appointments -->
              <mat-form-field class="full-width" appearance="outline">
                <mat-label>Related Subscription (Optional)</mat-label>
                <mat-select [(value)]="appointmentData.subId">
                  <mat-option value="">None</mat-option>
                  @for (sub of subscriptions; track sub.id) {
                    <mat-option [value]="sub.id">{{ sub.name }} - {{ sub.customer?.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }

            <div class="step-actions">
              <button mat-button matStepperPrevious>Back</button>
              <button mat-raised-button color="primary" type="submit" [disabled]="!isDetailsFormValid()">
                Create Appointment
              </button>
            </div>
          </form>
          
          @if (appointmentMessage) {
            <p class="success-message">{{ appointmentMessage }}</p>
          }
        </div>
      </mat-step>

    </mat-stepper>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button (click)="onCancel()">Cancel</button>
    @if (appointmentObj) {
      <button mat-raised-button color="primary" (click)="onComplete()">Finish</button>
    }
  </mat-dialog-actions>
</div>